package com.checkin.host.ui.auth

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.primaryButton
import com.checkin.host.ui.legal.AuthLegalFooter
import com.checkin.host.ui.legal.LegalAcceptanceCheckboxes
import com.checkin.host.ui.components.TextField
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.shared.dto.GoogleAuthRequest
import com.checkin.shared.dto.LoginRequest
import com.checkin.shared.dto.RegisterRequest
import com.checkin.shared.dto.UserDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AuthScreen(
    route: AuthRoute,
    onRouteChange: (AuthRoute) -> Unit,
    onAuthenticated: (UserDto) -> Unit,
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf("") }
    var acceptTerms by remember { mutableStateOf(false) }
    var acceptPrivacy by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var registrationEnabled by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        registrationEnabled = runCatching {
            ApiClient.fetchPublicAppConfig().registrationEnabled
        }.getOrDefault(true)
    }

    LaunchedEffect(route, registrationEnabled) {
        if (route == AuthRoute.Register && !registrationEnabled) {
            onRouteChange(AuthRoute.Login)
        }
    }

    val isRegister = route == AuthRoute.Register && registrationEnabled

    AuthCard(
        title = if (isRegister) "Créer un compte" else "Connexion",
        subtitle = "Gérez vos check-ins SmartCheckin",
    ) {
        error?.let { AlertBanner(it, isError = true) }

        if (!registrationEnabled) {
            AlertBanner(
                "Les inscriptions sont temporairement fermées. Connectez-vous si vous avez déjà un compte.",
                isError = false,
            )
        }

        if (isRegister) {
            Div({ style { marginBottom(12.px) } }) {
                TextField("Nom affiché", displayName) { displayName = it }
            }
        }

        Div({ style { marginBottom(12.px) } }) {
            TextField("Email", email, InputType.Email) { email = it }
        }

        Div({ style { marginBottom(12.px) } }) {
            TextField("Mot de passe", password, InputType.Password) { password = it }
        }

        if (isRegister) {
            Div({ style { marginBottom(12.px) } }) {
                TextField("Confirmer le mot de passe", confirmPassword, InputType.Password) {
                    confirmPassword = it
                }
            }
            LegalAcceptanceCheckboxes(
                returnRoute = AuthRoute.Register,
                acceptTerms = acceptTerms,
                acceptPrivacy = acceptPrivacy,
                onAcceptTermsChange = { acceptTerms = it },
                onAcceptPrivacyChange = { acceptPrivacy = it },
                onOpenLegal = onRouteChange,
            )
        } else {
            Div({ style { marginBottom(20.px) } })
        }

        Button({
            style {
                primaryButton()
                width(100.percent)
                property("opacity", if (loading) "0.7" else "1")
            }
            onClick {
                if (loading) return@onClick
                if (isRegister) {
                    when {
                        displayName.isBlank() -> error = "Nom requis"
                        email.isBlank() -> error = "Email requis"
                        password.length < 8 -> error = "Mot de passe minimum 8 caractères"
                        password != confirmPassword -> error = "Les mots de passe ne correspondent pas"
                        !acceptTerms || !acceptPrivacy -> error = "Acceptez les CGU et la politique de confidentialité"
                        else -> error = null
                    }
                    if (error != null) return@onClick
                }
                scope.launch {
                    loading = true
                    error = null
                    try {
                        if (isRegister) {
                            val response = ApiClient.register(
                                RegisterRequest(
                                    email = email.trim(),
                                    password = password,
                                    displayName = displayName.trim(),
                                    acceptTerms = acceptTerms,
                                    acceptPrivacy = acceptPrivacy,
                                ),
                            )
                            val token = response.token
                            val user = response.user
                            if (token != null && user != null) {
                                ApiClient.token = token
                                ApiClient.saveUser(user)
                                onAuthenticated(user)
                            } else {
                                onRouteChange(AuthRoute.VerifyPending(response.email ?: email.trim()))
                            }
                        } else {
                            val response = ApiClient.login(LoginRequest(email.trim(), password))
                            ApiClient.token = response.token
                            ApiClient.saveUser(response.user)
                            onAuthenticated(response.user)
                        }
                    } catch (e: ApiException) {
                        error = e.message
                    } catch (e: Exception) {
                        error = e.message ?: "Erreur réseau"
                    } finally {
                        loading = false
                    }
                }
            }
        }) {
            Text(if (loading) "..." else if (isRegister) "S'inscrire" else "Se connecter")
        }

        GoogleSignInSection(
            enabled = !loading && (!isRegister || (acceptTerms && acceptPrivacy)),
            onCredential = { idToken ->
                scope.launch {
                    loading = true
                    error = null
                    try {
                        val response = ApiClient.loginWithGoogle(
                            GoogleAuthRequest(
                                idToken = idToken,
                                acceptTerms = acceptTerms,
                                acceptPrivacy = acceptPrivacy,
                            ),
                        )
                        ApiClient.token = response.token
                        ApiClient.saveUser(response.user)
                        onAuthenticated(response.user)
                    } catch (e: ApiException) {
                        error = e.message
                    } catch (e: Exception) {
                        error = e.message ?: "Erreur réseau"
                    } finally {
                        loading = false
                    }
                }
            },
        )

        if (!isRegister) {
            P({
                style {
                    marginTop(12.px)
                    textAlign("center")
                    fontSize(14.px)
                    color(AppColors.primary)
                    cursor("pointer")
                }
                onClick { onRouteChange(AuthRoute.ForgotPassword) }
            }) { Text("Mot de passe oublié ?") }
        }

        if (registrationEnabled) {
            P({
                style {
                    marginTop(16.px)
                    textAlign("center")
                    fontSize(14.px)
                    color(AppColors.primary)
                    cursor("pointer")
                }
                onClick {
                    error = null
                    confirmPassword = ""
                    acceptTerms = false
                    acceptPrivacy = false
                    onRouteChange(if (isRegister) AuthRoute.Login else AuthRoute.Register)
                }
            }) {
                Text(
                    if (isRegister) "Déjà un compte ? Se connecter"
                    else "Pas de compte ? S'inscrire",
                )
            }
        }

        AuthLegalFooter(onOpenLegal = onRouteChange)
    }
}
